import type { BeneficiaryArchivedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryArchived';
import type { BeneficiaryCreatedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryCreated';
import type { BeneficiaryDeletedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryDeleted';
import type { DocumentDeletedAction } from '@Application/Beneficiaries/Documents/Actions/documentDeleted';
import type { DocumentValidatedAction } from '@Application/Beneficiaries/Documents/Actions/documentValidated';
import type { SubjectArchivedAction } from '@Application/Subjects/Archivals/Actions/subjectArchived';
import type { SubjectRestoredAction } from '@Application/Subjects/Archivals/Actions/subjectRestored';
import type { SubjectCreatedAction } from '@Application/Subjects/Actions/subjectCreated';
import type { WorkflowRunHandledAction } from '@Application/WorkflowRuns/Handling/Actions/workflowRunHandled';
import type { WorkflowRunUnhandledAction } from '@Application/WorkflowRuns/Handling/Actions/workflowRunUnhandled';
import type { WorkflowRunAbandonedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunAbandoned';
import type { WorkflowRunOpenedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunOpened';
import type { BeneficiaryIndicatorsFetchedAction } from '@Application/Indicators/Actions/beneficiaryIndicatorsFetched';
import type { DocumentIndicatorsFetchedAction } from '@Application/Indicators/Actions/documentIndicatorsFetched';
import type { CostIndicatorsFetchedAction } from '@Application/Indicators/Actions/costIndicatorsFetched';
import type { SubjectIndicatorsFetchedAction } from '@Application/Indicators/Actions/subjectIndicatorsFetched';
import type { WorkingFacilityCountsFetchedAction } from '@Application/Indicators/WorkingFacility/Actions/workingFacilityCountsFetched';
import type { HomeLeftAction } from '@Application/Indicators/Actions/homeLeft';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import BeneficiaryIndicatorsView from '@UI/Store/Indicators/Views/BeneficiaryIndicatorsView';
import DocumentIndicatorsView from '@UI/Store/Indicators/Views/DocumentIndicatorsView';
import CostIndicatorsView from '@UI/Store/Indicators/Views/CostIndicatorsView';
import SubjectIndicatorsView from '@UI/Store/Indicators/Views/SubjectIndicatorsView';
import WorkingFacilityCountsView from '@UI/Store/Indicators/Views/WorkingFacilityCountsView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type IndicatorsStore = {
	documents: DocumentIndicatorsView | null;
	beneficiaries: BeneficiaryIndicatorsView | null;
	costs: CostIndicatorsView | null;
	subjects: SubjectIndicatorsView | null;
	workingFacilityCounts: WorkingFacilityCountsView | null;
};

const initialState: IndicatorsStore = {
	documents: null,
	beneficiaries: null,
	costs: null,
	subjects: null,
	workingFacilityCounts: null,
};

type Actions =
	| DocumentIndicatorsFetchedAction
	| BeneficiaryIndicatorsFetchedAction
	| CostIndicatorsFetchedAction
	| SubjectIndicatorsFetchedAction
	| WorkingFacilityCountsFetchedAction
	| BeneficiaryCreatedAction
	| BeneficiaryArchivedAction
	| BeneficiaryDeletedAction
	| SubjectCreatedAction
	| SubjectArchivedAction
	| SubjectRestoredAction
	| DocumentValidatedAction
	| DocumentDeletedAction
	| WorkflowRunOpenedAction
	| WorkflowRunAbandonedAction
	| WorkflowRunHandledAction
	| WorkflowRunUnhandledAction
	| UserRevokedAction
	| HomeLeftAction;

const whenDocumentIndicatorsFetched = (
	store: IndicatorsStore,
	action: DocumentIndicatorsFetchedAction,
): IndicatorsStore => ({ ...store, documents: new DocumentIndicatorsView(action.payload) });

const whenBeneficiaryIndicatorsFetched = (
	store: IndicatorsStore,
	action: BeneficiaryIndicatorsFetchedAction,
): IndicatorsStore => ({ ...store, beneficiaries: new BeneficiaryIndicatorsView(action.payload) });

const whenCostIndicatorsFetched = (
	store: IndicatorsStore,
	action: CostIndicatorsFetchedAction,
): IndicatorsStore => ({ ...store, costs: new CostIndicatorsView(action.payload) });

const whenSubjectIndicatorsFetched = (
	store: IndicatorsStore,
	action: SubjectIndicatorsFetchedAction,
): IndicatorsStore => ({ ...store, subjects: new SubjectIndicatorsView(action.payload) });

const whenWorkingFacilityCountsFetched = (
	store: IndicatorsStore,
	action: WorkingFacilityCountsFetchedAction,
): IndicatorsStore => ({ ...store, workingFacilityCounts: new WorkingFacilityCountsView(action.payload) });

// A write elsewhere makes a counter stale; the home screen, not mounted during the gesture, reads it again.
const whenBeneficiariesChanged = (store: IndicatorsStore): IndicatorsStore => ({
	...store,
	beneficiaries: null,
	subjects: null,
});

const whenSubjectsChanged = (store: IndicatorsStore): IndicatorsStore => ({ ...store, subjects: null });

const whenDocumentsChanged = (store: IndicatorsStore): IndicatorsStore => ({ ...store, documents: null });

const whenWorkingFacilityChanged = (store: IndicatorsStore): IndicatorsStore => ({
	...store,
	workingFacilityCounts: null,
});

const whenUserRevoked = (): IndicatorsStore => initialState;

const whenHomeLeft = (): IndicatorsStore => initialState;

export default registerStore<'indicatorsStore', Actions>(
	'indicatorsStore',
	createSliceReducer({
		DOCUMENT_INDICATORS_FETCHED: whenDocumentIndicatorsFetched,
		BENEFICIARY_INDICATORS_FETCHED: whenBeneficiaryIndicatorsFetched,
		COST_INDICATORS_FETCHED: whenCostIndicatorsFetched,
		SUBJECT_INDICATORS_FETCHED: whenSubjectIndicatorsFetched,
		WORKING_FACILITY_COUNTS_FETCHED: whenWorkingFacilityCountsFetched,
		BENEFICIARY_CREATED: whenBeneficiariesChanged,
		BENEFICIARY_ARCHIVED: whenBeneficiariesChanged,
		BENEFICIARY_DELETED: whenBeneficiariesChanged,
		SUBJECT_CREATED: whenSubjectsChanged,
		SUBJECT_ARCHIVED: whenSubjectsChanged,
		SUBJECT_RESTORED: whenSubjectsChanged,
		DOCUMENT_VALIDATED: whenDocumentsChanged,
		DOCUMENT_DELETED: whenDocumentsChanged,
		WORKFLOW_RUN_OPENED: whenWorkingFacilityChanged,
		WORKFLOW_RUN_ABANDONED: whenWorkingFacilityChanged,
		WORKFLOW_RUN_HANDLED: whenWorkingFacilityChanged,
		WORKFLOW_RUN_UNHANDLED: whenWorkingFacilityChanged,
		USER_REVOKED: whenUserRevoked,
		HOME_LEFT: whenHomeLeft,
	}),
	initialState,
);
